import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";

function normalizeLocations(userDetail) {
    const explicitLocations = Array.isArray(userDetail?.accessibleLocations) ? userDetail.accessibleLocations : [];
    if (explicitLocations.length) {
        return explicitLocations
            .map((location) => ({
                id: Number(location.id),
                name: location.name || `Location ${location.id}`,
            }))
            .filter((location) => Number.isInteger(location.id) && location.id > 0);
    }

    const podiums = Array.isArray(userDetail?.accessiblePodiums) ? userDetail.accessiblePodiums : [];
    const ids = Array.from(new Set(podiums.map((podium) => Number(podium.locationId)).filter((id) => Number.isInteger(id) && id > 0)));
    return ids.map((id) => ({ id, name: `Location ${id}` }));
}

function buildRoutingForm() {
    return {
        stripeAccountId: "",
        payoutLabel: "",
        isActive: true,
    };
}

function buildReaderForm() {
    return {
        stripeReaderId: "",
        readerLabel: "",
        deviceType: "",
        isActive: true,
    };
}

const PaymentRoutingAdminPanel = () => {
    const { userDetail } = useContext(UserContext);
    const [selectedLocationId, setSelectedLocationId] = useState("");
    const [accountingEmail, setAccountingEmail] = useState("");
    const [connectCountry, setConnectCountry] = useState("US");
    const [includeInactiveReaders, setIncludeInactiveReaders] = useState(false);
    const [routingForm, setRoutingForm] = useState(buildRoutingForm());
    const [readerForm, setReaderForm] = useState(buildReaderForm());
    const [accountStatus, setAccountStatus] = useState(null);
    const [readers, setReaders] = useState([]);
    const [routingUpdatedAt, setRoutingUpdatedAt] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);
    const [isLaunchingOnboarding, setIsLaunchingOnboarding] = useState(false);
    const [isSavingRouting, setIsSavingRouting] = useState(false);
    const [isSavingReader, setIsSavingReader] = useState(false);

    const safeRoutingForm = routingForm && typeof routingForm === "object" ? routingForm : buildRoutingForm();
    const safeReaderForm = readerForm && typeof readerForm === "object" ? readerForm : buildReaderForm();
    const routingStripeAccountId = String(safeRoutingForm.stripeAccountId || "");
    const routingIsActive = Boolean(safeRoutingForm.isActive);

    const checklistState = useMemo(() => {
        const hasConnectedAccount = Boolean(routingStripeAccountId);
        const detailsSubmitted = Boolean(accountStatus?.detailsSubmitted);
        const paymentsEnabled = Boolean(accountStatus?.chargesEnabled);
        const payoutsEnabled = Boolean(accountStatus?.payoutsEnabled);
        const payoutReady = Boolean(accountStatus?.payoutReady);
        const routingActive = routingIsActive;

        const items = [
            { key: "account", label: "Connected account created", done: hasConnectedAccount },
            { key: "details", label: "Onboarding details submitted", done: detailsSubmitted },
            { key: "capabilities", label: "Charges and payouts enabled", done: paymentsEnabled && payoutsEnabled },
            { key: "payoutReady", label: "Payout ready", done: payoutReady },
            { key: "routing", label: "Routing enabled", done: routingActive },
        ];

        const pendingItems = items.filter((item) => !item.done);
        const isComplete = pendingItems.length === 0;

        return {
            items,
            pendingItems,
            isComplete,
            requirementsCurrentlyDue: Array.isArray(accountStatus?.requirementsCurrentlyDue) ? accountStatus.requirementsCurrentlyDue : [],
            requirementsPastDue: Array.isArray(accountStatus?.requirementsPastDue) ? accountStatus.requirementsPastDue : [],
            disabledReason: accountStatus?.requirementsDisabledReason || "",
        };
    }, [accountStatus, routingStripeAccountId, routingIsActive]);

    const accessibleLocations = useMemo(() => normalizeLocations(userDetail), [userDetail]);

    useEffect(() => {
        if (!accessibleLocations.length) {
            setSelectedLocationId("");
            return;
        }

        const preferredId = Number(userDetail?.locationId || accessibleLocations[0].id);
        const hasPreferred = accessibleLocations.some((location) => location.id === preferredId);
        setSelectedLocationId(String(hasPreferred ? preferredId : accessibleLocations[0].id));
    }, [accessibleLocations, userDetail]);

    useEffect(() => {
        async function loadLocationRoutingAndReaders() {
            if (!selectedLocationId) {
                setRoutingForm(buildRoutingForm());
                setReaderForm(buildReaderForm());
                setAccountStatus(null);
                setReaders([]);
                setRoutingUpdatedAt(null);
                return;
            }

            try {
                setIsLoading(true);
                const locationId = Number(selectedLocationId);
                const [statusRes, readersRes] = await Promise.all([ValetApi.getPaymentConnectStatus(locationId), ValetApi.getPaymentReadersByLocation(locationId, includeInactiveReaders)]);

                const routing = statusRes?.data?.routing || null;
                const loadedAccountStatus = statusRes?.data?.accountStatus || null;
                const loadedReaders = Array.isArray(readersRes?.data?.readers) ? readersRes.data.readers : [];

                setRoutingForm({
                    stripeAccountId: routing?.stripeAccountId || "",
                    payoutLabel: routing?.payoutLabel || "",
                    isActive: routing?.isActive !== false,
                });
                setAccountStatus(loadedAccountStatus);
                setConnectCountry(String(loadedAccountStatus?.country || "US").toUpperCase());
                setRoutingUpdatedAt(routing?.updatedAt || null);
                setReaders(loadedReaders);
            } catch (err) {
                setRoutingForm(buildRoutingForm());
                setAccountStatus(null);
                setRoutingUpdatedAt(null);
                setReaders([]);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load payment routing", { autoClose: 1400 });
            } finally {
                setIsLoading(false);
            }
        }

        loadLocationRoutingAndReaders();
    }, [selectedLocationId, includeInactiveReaders]);

    async function refreshAccountStatus(options = {}) {
        if (!selectedLocationId) return;

        try {
            if (!options.silent) {
                setIsRefreshingStatus(true);
            }

            const locationId = Number(selectedLocationId);
            const statusRes = await ValetApi.getPaymentConnectStatus(locationId);
            const routing = statusRes?.data?.routing || null;
            const loadedAccountStatus = statusRes?.data?.accountStatus || null;

            setRoutingForm((prev) => ({
                ...(prev && typeof prev === "object" ? prev : buildRoutingForm()),
                stripeAccountId: routing?.stripeAccountId || prev?.stripeAccountId || "",
                payoutLabel: routing?.payoutLabel || prev?.payoutLabel || "",
                isActive: routing?.isActive !== false,
            }));
            setRoutingUpdatedAt(routing?.updatedAt || null);
            setAccountStatus(loadedAccountStatus);
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to refresh account status", { autoClose: 1300 });
        } finally {
            if (!options.silent) {
                setIsRefreshingStatus(false);
            }
        }
    }

    async function handleCreateConnectedAccount() {
        if (!selectedLocationId) {
            toast.error("Select a location first", { autoClose: 1200 });
            return;
        }

        try {
            setIsCreatingAccount(true);
            const locationId = Number(selectedLocationId);
            const res = await ValetApi.createPaymentConnectAccount(locationId, {
                email: String(accountingEmail || "").trim(),
                country: String(connectCountry || "US")
                    .trim()
                    .toUpperCase(),
                payoutLabel: String(safeRoutingForm.payoutLabel || "").trim(),
            });

            const account = res?.data?.accountStatus || null;
            const routing = res?.data?.routing || null;
            if (routing) {
                setRoutingForm((prev) => ({
                    ...(prev && typeof prev === "object" ? prev : buildRoutingForm()),
                    stripeAccountId: routing.stripeAccountId || prev?.stripeAccountId || "",
                    payoutLabel: routing.payoutLabel || prev?.payoutLabel || "",
                    isActive: routing.isActive !== false,
                }));
                setRoutingUpdatedAt(routing.updatedAt || null);
            }
            setAccountStatus(account);
            toast.success("Connected account created. Complete onboarding next.", { autoClose: 1400 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to create Stripe connected account", { autoClose: 1500 });
        } finally {
            setIsCreatingAccount(false);
        }
    }

    async function handleLaunchOnboarding() {
        if (!selectedLocationId) {
            toast.error("Select a location first", { autoClose: 1200 });
            return;
        }

        try {
            setIsLaunchingOnboarding(true);
            const locationId = Number(selectedLocationId);
            const res = await ValetApi.createPaymentConnectOnboardingLink(locationId, {});
            const link = res?.data?.link || null;

            if (!link?.url) {
                throw new Error("Onboarding link was not returned");
            }

            window.open(link.url, "_blank", "noopener,noreferrer");
            toast.success("Opened Stripe onboarding in a new tab", { autoClose: 1300 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to launch onboarding", { autoClose: 1500 });
        } finally {
            setIsLaunchingOnboarding(false);
        }
    }

    async function handleSaveRouting(evt) {
        evt.preventDefault();
        if (!selectedLocationId) {
            toast.error("Select a location first", { autoClose: 1200 });
            return;
        }

        const stripeAccountId = String(safeRoutingForm.stripeAccountId || "").trim();
        if (!stripeAccountId) {
            toast.error("Stripe account id is required", { autoClose: 1200 });
            return;
        }

        if (routingIsActive && accountStatus && !accountStatus.payoutReady) {
            toast.error("Account is not payout-ready yet. Complete onboarding and refresh status before enabling routing.", { autoClose: 1700 });
            return;
        }

        try {
            setIsSavingRouting(true);
            const res = await ValetApi.upsertPaymentLocationRouting(Number(selectedLocationId), {
                stripeAccountId,
                payoutLabel: String(safeRoutingForm.payoutLabel || "").trim(),
                isActive: routingIsActive,
            });

            const saved = res?.data?.routing || null;
            const savedAccountStatus = res?.data?.accountStatus || null;
            setRoutingForm({
                stripeAccountId: saved?.stripeAccountId || stripeAccountId,
                payoutLabel: saved?.payoutLabel || "",
                isActive: saved?.isActive !== false,
            });
            if (savedAccountStatus) {
                setAccountStatus(savedAccountStatus);
            }
            setRoutingUpdatedAt(saved?.updatedAt || null);
            toast.success("Location routing saved", { autoClose: 1200 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to save location routing", { autoClose: 1400 });
        } finally {
            setIsSavingRouting(false);
        }
    }

    async function handleSaveReader(evt) {
        evt.preventDefault();
        if (!selectedLocationId) {
            toast.error("Select a location first", { autoClose: 1200 });
            return;
        }

        const stripeAccountId = String(safeRoutingForm.stripeAccountId || "").trim();
        if (!stripeAccountId) {
            toast.error("Save routing account before adding readers", { autoClose: 1200 });
            return;
        }

        const stripeReaderId = String(safeReaderForm.stripeReaderId || "").trim();
        if (!stripeReaderId) {
            toast.error("Reader id is required", { autoClose: 1200 });
            return;
        }

        try {
            setIsSavingReader(true);
            await ValetApi.upsertPaymentTerminalReader(stripeReaderId, {
                locationId: Number(selectedLocationId),
                stripeAccountId,
                readerLabel: String(safeReaderForm.readerLabel || "").trim(),
                deviceType: String(safeReaderForm.deviceType || "").trim(),
                isActive: Boolean(safeReaderForm.isActive),
            });

            const readersRes = await ValetApi.getPaymentReadersByLocation(Number(selectedLocationId), includeInactiveReaders);
            setReaders(Array.isArray(readersRes?.data?.readers) ? readersRes.data.readers : []);
            setReaderForm(buildReaderForm());
            toast.success("Reader saved", { autoClose: 1200 });
        } catch (err) {
            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to save reader", { autoClose: 1400 });
        } finally {
            setIsSavingReader(false);
        }
    }

    function updateRoutingField(field, value) {
        setRoutingForm((prev) => ({
            ...(prev && typeof prev === "object" ? prev : buildRoutingForm()),
            [field]: value,
        }));
    }

    function updateReaderField(field, value) {
        setReaderForm((prev) => ({
            ...(prev && typeof prev === "object" ? prev : buildReaderForm()),
            [field]: value,
        }));
    }

    return (
        <div className="payment-routing-panel">
            <h3>Location Routing and Readers</h3>
            <p className="payment-routing-help">Tie each location to one Stripe account, then register each reader under that location/account pair.</p>
            <div className="payment-connect-steps">
                <div className="payment-connect-step">1. Create connected account</div>
                <div className="payment-connect-step">2. Send accountant through Stripe onboarding</div>
                <div className="payment-connect-step">3. Refresh status and verify payout readiness</div>
                <div className="payment-connect-step">4. Enable routing only when payout-ready</div>
            </div>

            <div className="payment-connect-onboarding-box">
                <div className="payment-routing-readers-header">
                    <h4>Accountant Onboarding</h4>
                </div>

                <div className="payment-config-grid payment-config-grid-three">
                    <FormGroup>
                        <Label for="connectAccountantEmail">Owner/Accountant Email</Label>
                        <Input
                            id="connectAccountantEmail"
                            type="email"
                            value={accountingEmail}
                            placeholder="owner@client.com"
                            onChange={(evt) => setAccountingEmail(evt.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="connectCountry">Country</Label>
                        <Input
                            id="connectCountry"
                            value={connectCountry}
                            maxLength={2}
                            onChange={(evt) => setConnectCountry(String(evt.target.value || "").toUpperCase())}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Actions</Label>
                        <div className="payment-connect-action-buttons">
                            <Button
                                color="primary"
                                type="button"
                                onClick={handleCreateConnectedAccount}
                                disabled={isCreatingAccount || !selectedLocationId || Boolean(routingStripeAccountId)}>
                                {isCreatingAccount ? "Creating..." : "Create Connected Account"}
                            </Button>
                            <Button
                                color="secondary"
                                type="button"
                                onClick={handleLaunchOnboarding}
                                disabled={isLaunchingOnboarding || !selectedLocationId || !routingStripeAccountId}>
                                {isLaunchingOnboarding ? "Opening..." : "Open Onboarding"}
                            </Button>
                            <Button
                                color="secondary"
                                type="button"
                                onClick={() => refreshAccountStatus()}
                                disabled={isRefreshingStatus || !selectedLocationId || !routingStripeAccountId}>
                                {isRefreshingStatus ? "Refreshing..." : "Refresh Status"}
                            </Button>
                        </div>
                    </FormGroup>
                </div>

                {routingStripeAccountId ? <div className="payment-routing-meta">Connected Account: {routingStripeAccountId}</div> : null}

                <div className={`payment-connect-checklist-banner ${checklistState.isComplete ? "is-complete" : "is-pending"}`}>
                    <div className="payment-connect-checklist-title">Onboarding Checklist: {checklistState.isComplete ? "Complete" : "Pending"}</div>
                    <div className="payment-connect-checklist-grid">
                        {checklistState.items.map((item) => (
                            <span
                                key={item.key}
                                className={`payment-connect-checklist-item ${item.done ? "is-done" : "is-missing"}`}>
                                {item.done ? "Done" : "Pending"}: {item.label}
                            </span>
                        ))}
                    </div>

                    {checklistState.pendingItems.length && accountStatus ? <div className="payment-connect-checklist-hint">Finish Stripe onboarding and click Refresh Status before enabling routing.</div> : null}

                    {checklistState.requirementsCurrentlyDue.length ? <div className="payment-connect-checklist-hint">Currently due: {checklistState.requirementsCurrentlyDue.join(", ")}</div> : null}

                    {checklistState.requirementsPastDue.length ? <div className="payment-connect-checklist-hint">Past due: {checklistState.requirementsPastDue.join(", ")}</div> : null}

                    {checklistState.disabledReason ? <div className="payment-connect-checklist-hint">Disabled reason: {checklistState.disabledReason}</div> : null}
                </div>

                {accountStatus ? (
                    <div className="payment-connect-status-grid">
                        <span className={`payment-connect-pill ${accountStatus.payoutReady ? "is-good" : "is-warn"}`}>Payout Ready: {accountStatus.payoutReady ? "Yes" : "No"}</span>
                        <span className={`payment-connect-pill ${accountStatus.detailsSubmitted ? "is-good" : "is-warn"}`}>Details Submitted: {accountStatus.detailsSubmitted ? "Yes" : "No"}</span>
                        <span className={`payment-connect-pill ${accountStatus.chargesEnabled ? "is-good" : "is-warn"}`}>Charges Enabled: {accountStatus.chargesEnabled ? "Yes" : "No"}</span>
                        <span className={`payment-connect-pill ${accountStatus.payoutsEnabled ? "is-good" : "is-warn"}`}>Payouts Enabled: {accountStatus.payoutsEnabled ? "Yes" : "No"}</span>
                        <span className="payment-connect-pill">Currently Due: {(accountStatus.requirementsCurrentlyDue || []).length}</span>
                        <span className="payment-connect-pill">Past Due: {(accountStatus.requirementsPastDue || []).length}</span>
                    </div>
                ) : null}

                {!accountStatus && routingStripeAccountId ? <div className="payment-routing-meta">Status unavailable. Click Refresh Status to fetch latest verification state.</div> : null}
            </div>

            <Form onSubmit={handleSaveRouting}>
                <div className="payment-config-grid payment-config-grid-three">
                    <FormGroup>
                        <Label for="routingLocationId">Location</Label>
                        <Input
                            id="routingLocationId"
                            type="select"
                            value={selectedLocationId}
                            onChange={(evt) => setSelectedLocationId(evt.target.value)}>
                            {accessibleLocations.length ? null : <option value="">No locations available</option>}
                            {accessibleLocations.map((location) => (
                                <option
                                    key={location.id}
                                    value={location.id}>
                                    {location.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label for="routingStripeAccount">Stripe Account ID</Label>
                        <Input
                            id="routingStripeAccount"
                            value={safeRoutingForm.stripeAccountId}
                            placeholder="acct_..."
                            onChange={(evt) => updateRoutingField("stripeAccountId", evt.currentTarget.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="routingPayoutLabel">Payout Label</Label>
                        <Input
                            id="routingPayoutLabel"
                            value={safeRoutingForm.payoutLabel}
                            placeholder="Owner / location payout label"
                            onChange={(evt) => updateRoutingField("payoutLabel", evt.currentTarget.value)}
                        />
                    </FormGroup>
                </div>

                <FormGroup
                    check
                    className="payment-routing-checkbox-row">
                    <input
                        id="routingIsActive"
                        type="checkbox"
                        className="payment-routing-checkbox"
                        checked={routingIsActive}
                        onChange={(evt) => updateRoutingField("isActive", evt.currentTarget.checked)}
                    />
                    <label
                        className="form-check-label payment-routing-checkbox-label"
                        htmlFor="routingIsActive">
                        Routing active for this location
                    </label>
                </FormGroup>

                <div className="payment-routing-actions-row">
                    <Button
                        color="primary"
                        type="submit"
                        disabled={isSavingRouting || !selectedLocationId}>
                        {isSavingRouting ? "Saving Routing..." : "Save Location Routing"}
                    </Button>
                    {routingUpdatedAt ? <span className="payment-routing-meta">Routing updated: {new Date(routingUpdatedAt).toLocaleString()}</span> : null}
                </div>
            </Form>

            <hr />

            <Form onSubmit={handleSaveReader}>
                <div className="payment-routing-readers-header">
                    <h4>Terminal Readers</h4>
                    <div className="payment-routing-include-inactive">
                        <input
                            id="includeInactiveReaders"
                            type="checkbox"
                            className="payment-routing-checkbox"
                            checked={includeInactiveReaders}
                            onChange={(evt) => setIncludeInactiveReaders(evt.target.checked)}
                        />
                        <label
                            className="form-check-label payment-routing-checkbox-label"
                            htmlFor="includeInactiveReaders">
                            Show inactive readers
                        </label>
                    </div>
                </div>

                <div className="payment-config-grid payment-config-grid-three">
                    <FormGroup>
                        <Label for="readerStripeId">Stripe Reader ID</Label>
                        <Input
                            id="readerStripeId"
                            value={safeReaderForm.stripeReaderId}
                            placeholder="tmr_..."
                            onChange={(evt) => updateReaderField("stripeReaderId", evt.currentTarget.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="readerLabel">Reader Label</Label>
                        <Input
                            id="readerLabel"
                            value={safeReaderForm.readerLabel}
                            placeholder="Front desk iPhone"
                            onChange={(evt) => updateReaderField("readerLabel", evt.currentTarget.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="readerDeviceType">Device Type</Label>
                        <Input
                            id="readerDeviceType"
                            value={safeReaderForm.deviceType}
                            placeholder="ios / android / wisepad"
                            onChange={(evt) => updateReaderField("deviceType", evt.currentTarget.value)}
                        />
                    </FormGroup>
                </div>

                <FormGroup
                    check
                    className="payment-routing-checkbox-row">
                    <input
                        id="readerIsActive"
                        type="checkbox"
                        className="payment-routing-checkbox"
                        checked={Boolean(safeReaderForm.isActive)}
                        onChange={(evt) => updateReaderField("isActive", evt.currentTarget.checked)}
                    />
                    <label
                        className="form-check-label payment-routing-checkbox-label"
                        htmlFor="readerIsActive">
                        Reader active
                    </label>
                </FormGroup>

                <Button
                    color="primary"
                    type="submit"
                    disabled={isSavingReader || !selectedLocationId}>
                    {isSavingReader ? "Saving Reader..." : "Save Reader"}
                </Button>
            </Form>

            <div className="payment-routing-readers-table-wrap">
                {isLoading ? <p>Loading routing and readers...</p> : null}
                {!isLoading && !readers.length ? <p>No readers configured for this location.</p> : null}
                {!isLoading && readers.length ? (
                    <table className="payment-routing-readers-table">
                        <thead>
                            <tr>
                                <th>Reader ID</th>
                                <th>Label</th>
                                <th>Device</th>
                                <th>Account</th>
                                <th>Status</th>
                                <th>Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {readers.map((reader) => (
                                <tr key={reader.stripeReaderId}>
                                    <td>{reader.stripeReaderId}</td>
                                    <td>{reader.readerLabel || "--"}</td>
                                    <td>{reader.deviceType || "--"}</td>
                                    <td>{reader.stripeAccountId || "--"}</td>
                                    <td>{reader.isActive ? "Active" : "Inactive"}</td>
                                    <td>{reader.updatedAt ? new Date(reader.updatedAt).toLocaleString() : "--"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : null}
            </div>
        </div>
    );
};

export default PaymentRoutingAdminPanel;
